import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTours } from '@/hooks/useTours';
import { useExperiences } from '@/hooks/useExperiences';
import { useDestinations } from '@/hooks/useDestinations';
import { useBlogPosts } from '@/hooks/useBlogPosts';

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

interface SearchResult {
  key: string;
  type: 'tour' | 'destination' | 'experience' | 'blog';
  title: string;
  subtitle: string;
  image: string;
  href: string;
}

// Strip Vietnamese diacritics so "da nang" matches "Đà Nẵng".
function normalize(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd');
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { tours } = useTours();
  const { experiences } = useExperiences();
  const { destinations } = useDestinations();
  const { posts } = useBlogPosts();

  // Lock body scroll + focus input + reset query whenever the overlay opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => {
      document.body.style.overflow = '';
      window.clearTimeout(timer);
    };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const results = useMemo<SearchResult[]>(() => {
    const q = normalize(query.trim());
    if (!q) return [];

    const items: SearchResult[] = [];

    tours.forEach((tour) => {
      const haystack = normalize(`${tour.title} ${tour.category} ${tour.group_type} ${tour.duration}`);
      if (haystack.includes(q)) {
        items.push({
          key: `tour-${tour.id}`,
          type: 'tour',
          title: tour.title,
          subtitle: `${tour.duration} · ${tour.category}`,
          image: tour.image,
          href: `/tour/${tour.id}`,
        });
      }
    });

    destinations.forEach((dest) => {
      const haystack = normalize(`${dest.name} ${dest.description}`);
      if (haystack.includes(q)) {
        items.push({
          key: `dest-${dest.id}`,
          type: 'destination',
          title: dest.name,
          subtitle: dest.description,
          image: dest.image,
          href: `/tours?destination=${dest.id}`,
        });
      }
    });

    experiences.forEach((exp) => {
      const haystack = normalize(`${exp.title} ${exp.category} ${exp.group_type} ${exp.region}`);
      if (haystack.includes(q)) {
        items.push({
          key: `exp-${exp.id}`,
          type: 'experience',
          title: exp.title,
          subtitle: `${exp.region} · ${exp.category}`,
          image: exp.image,
          href: `/experience/${exp.id}`,
        });
      }
    });

    posts.forEach((post) => {
      const haystack = normalize(`${post.title} ${post.excerpt} ${post.category}`);
      if (haystack.includes(q)) {
        items.push({
          key: `blog-${post.id}`,
          type: 'blog',
          title: post.title,
          subtitle: post.category,
          image: post.image,
          href: `/blog/${post.slug}`,
        });
      }
    });

    return items.slice(0, 30);
  }, [query, tours, experiences, destinations, posts]);

  // Popular suggestions shown before the user types anything.
  const popular = useMemo<SearchResult[]>(() => {
    const items: SearchResult[] = [
      ...tours.slice(0, 4).map((tour) => ({
        key: `tour-${tour.id}`,
        type: 'tour' as const,
        title: tour.title,
        subtitle: `${tour.duration} · ${tour.category}`,
        image: tour.image,
        href: `/tour/${tour.id}`,
      })),
      ...destinations.slice(0, 4).map((dest) => ({
        key: `dest-${dest.id}`,
        type: 'destination' as const,
        title: dest.name,
        subtitle: dest.description,
        image: dest.image,
        href: `/tours?destination=${dest.id}`,
      })),
    ];
    return items;
  }, [tours, destinations]);

  if (!open) return null;

  const sectionMeta: Record<SearchResult['type'], { label: string; icon: string }> = {
    tour: { label: t('search.tours'), icon: 'ri-suitcase-line' },
    destination: { label: t('search.destinations'), icon: 'ri-map-pin-line' },
    experience: { label: t('search.experiences'), icon: 'ri-compass-3-line' },
    blog: { label: t('search.blog'), icon: 'ri-article-line' },
  };

  const order: SearchResult['type'][] = ['tour', 'destination', 'experience', 'blog'];

  const grouped = order
    .map((type) => ({ type, items: results.filter((r) => r.type === type) }))
    .filter((g) => g.items.length > 0);

  const hasQuery = query.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[10vh] md:pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label={t('search.title')}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Input row */}
        <div className="flex items-center gap-3 border-b border-background-200/70 px-4 py-3.5">
          <i className="ri-search-line text-xl text-secondary-400" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search.placeholder')}
            className="h-10 min-w-0 flex-1 bg-transparent text-base text-foreground-950 outline-none placeholder:text-secondary-400"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label={t('search.close')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-secondary-500 transition-colors hover:bg-background-100 hover:text-foreground-950"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Results body */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!hasQuery ? (
            <div className="py-2">
              <div className="flex items-center gap-2 px-4 py-3">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-background-100 text-secondary-500">
                  <i className="ri-fire-line text-sm" />
                </span>
                <h4 className="text-xs font-bold uppercase tracking-wide text-secondary-500">
                  {t('search.popular')}
                </h4>
              </div>
              {popular.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  onClick={onClose}
                  className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-background-100"
                >
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-background-100">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-secondary-400">
                        <i className={`${sectionMeta[item.type].icon} text-lg`} />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground-950">
                      {item.title}
                    </p>
                    <p className="truncate text-xs text-secondary-500">{item.subtitle}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-lg text-secondary-300" />
                </Link>
              ))}
            </div>
          ) : grouped.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-secondary-400" />
              </div>
              <p className="text-sm text-secondary-500">
                {t('search.noResults', { query: query.trim() })}
              </p>
            </div>
          ) : (
            <div className="py-2">
              {grouped.map((group) => (
                <div key={group.type} className="mb-1">
                  <div className="flex items-center gap-2 px-4 py-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-background-100 text-secondary-500">
                      <i className={`${sectionMeta[group.type].icon} text-sm`} />
                    </span>
                    <h4 className="text-xs font-bold uppercase tracking-wide text-secondary-500">
                      {sectionMeta[group.type].label}
                    </h4>
                    <span className="text-xs text-secondary-400">{group.items.length}</span>
                  </div>
                  {group.items.map((item) => (
                    <Link
                      key={item.key}
                      to={item.href}
                      onClick={onClose}
                      className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-background-100">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-secondary-400">
                            <i className={`${sectionMeta[item.type].icon} text-lg`} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground-950">
                          {item.title}
                        </p>
                        <p className="truncate text-xs text-secondary-500">{item.subtitle}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-lg text-secondary-300" />
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}